import React, { useEffect, useState } from 'react';

import { adminRights as defaultAdminRight } from 'utils/constants';
import { ADMINRIGHTS, AUTH, USERPROFILE } from 'utils/rest';

import { cleanLocalStorage } from 'containers/AuthContainer';

const { getUser, logout } = AUTH;

const UserContext = React.createContext();
const requestStates = {
  LOADING: 'loading',
  LOADED: 'loaded',
  NOT_LAUNCH: 'not_launch'
};

const { LOADING, LOADED, NOT_LAUNCH } = requestStates;

/**
 * UserProvider
 * @component
 */
const UserProvider = props => {
  const [user, setUser] = useState({});
  const [profile, setProfile] = useState();
  const [adminRights, setAdminRights] = useState({});
  const [userLocation, setUserLocation] = useState(window.location.pathname);
  const [requestState, setRequestState] = useState(LOADING);
  const [intervalId, setIntervalId] = useState(null);

  const updateProfile = async profileKey => {
    if (profileKey) {
      const userProfile = await USERPROFILE.getByKey(profileKey);
      if (userProfile) {
        setProfile(userProfile);
        return;
      }
    }
    setProfile({});
  };

  const fetchUser = async () => {
    try {
      const response = await getUser();
      const currentUser = response.data;
      // Laisser avant le set de l'user pour eviter le setSimplify avant d'avoir le profil
      await updateProfile(currentUser.profile);

      setUser({
        ...currentUser,
        roles: currentUser.roles || []
      });

      const data = await ADMINRIGHTS.getRightsById(currentUser.id);
      setAdminRights(data?.[0]?.rights || defaultAdminRight);

      setRequestState(LOADED);
      return { ...currentUser, roles: currentUser.roles || [] };
    } catch (e) {
      setRequestState(NOT_LAUNCH);
      return {};
    }
  };

  const lightFetch = async () => {
    if (user?.domain) {
      const response = await getUser();
      const currentUser = response?.data;
      if (currentUser?.domain !== user.domain) {
        await fetchUser();
        window.location.href = '/dashboard';
      }
    }
  };

  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user?.domain) {
      if (intervalId) {
        clearInterval(intervalId);
      }
      setIntervalId(setInterval(lightFetch, 5000));
    } else {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    // eslint-disable-next-line
  }, [user]);

  const signIn = async ({ email, password }) => {
    try {
      const data = await AUTH.login(email, password);
      return data;
    } catch (e) {
      console.error(e);
      return { success: false, error: e };
    }
  };

  const logOut = async () => {
    setUser({});
    try {
      await logout();
      cleanLocalStorage();
      setRequestState(NOT_LAUNCH);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('err sign out  user (userProvider)', err);
      }
    }
  };

  const value = {
    isAuthenticated: !!user.id,
    isSuperAdmin: user?.roles?.includes('SUPERADMIN'),
    isLoadingAuth: requestState === LOADING,
    logOut,
    signIn,
    fetchUser,
    user,
    profile,
    userLocation,
    setUserLocation,
    adminRights,
    setAdminRights
  };
  return <UserContext.Provider {...props} value={value} />;
};

export default UserProvider;

export const useUser = () => {
  const context = React.useContext(UserContext);
  if (!context) throw new Error('UserContext must be in UserProvider');
  return context;
};
